import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BrandingSettings, BrandingColors, BrandingCustomCopy } from '../types/branding.ts';

export default function PreviewPage() {
  const [searchParams] = useSearchParams();
  const [branding, setBranding] = useState<BrandingSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBrandingFromParams = () => {
      try {
        const preview = searchParams.get('preview');
        if (preview !== 'true') return setIsLoading(false);

        const colorsParam = searchParams.get('colors');
        const companyName = searchParams.get('company_name') || 'Schedule Hero AI';
        const logoUrl = searchParams.get('logo_url') || '';
        const customCopyParam = searchParams.get('custom_copy');

        let colors: BrandingColors = {
          primary: '#5B6FED',
          secondary: '#8B5CF6',
          accent: '#06B6D4',
          background: '#FFFFFF',
          foreground: '#000000',
        };

        let customCopy: BrandingCustomCopy = {
          welcomeMessage: 'Welcome to our AI scheduling platform',
          aiName: 'Jade',
          ctaText: 'Start Your Free Trial',
          tagline: 'Intelligent scheduling made simple',
          aiGreeting: 'Hi, thank you for calling, this is Jade, the AI assistant. How can I help?',
          successTitle: 'Success! Your AI Employee is Ready!',
          successMessage: 'is now active and ready to help your customers.',
          getStartedText: 'Get Started',
          welcomeTitle: "Welcome! Let's set up your AI Employee.",
          welcomeSubtitle: "We'll get your AI ready to handle calls in about 5 minutes. We just need a few key details to start.",
          dashboardButtonText: 'Go to Dashboard',
        };

        if (colorsParam) {
          colors = { ...colors, ...JSON.parse(colorsParam) };
        }

        if (customCopyParam) {
          customCopy = { ...customCopy, ...JSON.parse(customCopyParam) };
        }

        // Get dynamic values from current domain
        const currentDomain = window.location.hostname;
        const currentBaseUrl = window.location.origin;
        
        const brandingSettings: BrandingSettings = {
          colors,
          companyName,
          logoUrl: logoUrl || undefined,
          customCopy,
          fromEmail: `no-reply@${currentDomain}`,
          replyToEmail: `support@${currentDomain}`,
          emailStatus: 'active',
          baseUrl: currentBaseUrl,
        };

        setBranding(brandingSettings);
        applyBrandingToDOM(brandingSettings);
      } catch (error) {
        console.error('Error parsing branding parameters:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBrandingFromParams();
  }, [searchParams]);

  const applyBrandingToDOM = (settings: BrandingSettings) => {
    const root = document.documentElement;
    
    // Apply CSS custom properties
    root.style.setProperty('--primary', settings.colors.primary);
    root.style.setProperty('--secondary', settings.colors.secondary);
    root.style.setProperty('--accent', settings.colors.accent);
    root.style.setProperty('--background', settings.colors.background);
    root.style.setProperty('--foreground', settings.colors.foreground);

    // Update page title
    document.title = `${settings.companyName} - Preview`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (!branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Preview Not Available</h1>
          <p className="text-gray-600">Invalid preview parameters or branding not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              {branding?.logoUrl ? (
                <img 
                  src={branding.logoUrl}
                  alt={branding?.companyName || 'Company Logo'}
                  className="h-8 w-auto"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <h1 className="text-2xl font-bold" style={{ color: branding?.colors?.primary || '#5B6FED' }}>
                  {branding?.companyName || 'Schedule Hero AI'}
                </h1>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <button
                className="px-4 py-2 text-sm font-medium rounded-md"
                style={{
                  backgroundColor: branding?.colors?.primary || '#5B6FED',
                  color: '#FFFFFF'
                }}
              >
                {branding?.customCopy?.ctaText || 'Get Started'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: branding?.colors?.background || '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-6" style={{ color: branding?.colors?.foreground || '#000000' }}>
            {branding?.customCopy?.welcomeMessage || 'Welcome to our AI scheduling platform'}
          </h1>
          <p className="text-xl mb-8" style={{ color: branding?.colors?.foreground || '#000000' }}>
            {branding?.customCopy?.tagline || 'Intelligent scheduling made simple'}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              className="px-8 py-3 text-lg font-medium rounded-lg"
              style={{
                backgroundColor: branding?.colors?.primary || '#5B6FED',
                color: '#FFFFFF'
              }}
            >
              {branding?.customCopy?.ctaText || 'Get Started'}
            </button>
            <button
              className="px-8 py-3 text-lg font-medium rounded-lg border-2"
              style={{
                borderColor: branding?.colors?.secondary || '#8B5CF6',
                color: branding?.colors?.secondary || '#8B5CF6'
              }}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4" style={{ color: branding?.colors?.foreground || '#000000' }}>
              Why Choose {branding?.companyName || 'Schedule Hero AI'}?
            </h2>
            <p className="text-lg" style={{ color: branding?.colors?.foreground || '#000000' }}>
              {branding?.customCopy?.tagline || 'Experience the future of intelligent scheduling'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: branding?.colors?.accent || '#06B6D4' }}
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: branding?.colors?.foreground || '#000000' }}>
                24/7 Availability
              </h3>
              <p className="text-gray-600">
                Your AI assistant {branding?.customCopy?.aiName || 'Jade'} is always ready to help schedule appointments
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: branding?.colors?.secondary || '#8B5CF6' }}
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: branding?.colors?.foreground || '#000000' }}>
                Intelligent Scheduling
              </h3>
              <p className="text-gray-600">
                Smart algorithms that understand your business and optimize scheduling
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: branding?.colors?.primary || '#5B6FED' }}
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: branding?.colors?.foreground || '#000000' }}>
                Lightning Fast
              </h3>
              <p className="text-gray-600">
                Instant responses and real-time scheduling updates
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: branding?.colors?.primary || '#5B6FED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Join thousands of businesses using {branding?.companyName || 'Schedule Hero AI'} to streamline their scheduling
          </p>
          <button className="px-8 py-3 text-lg font-medium bg-white rounded-lg" style={{ color: branding?.colors?.primary || '#5B6FED' }}>
            {branding?.customCopy?.ctaText || 'Get Started'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-400">
              © 2024 {branding?.companyName || 'Schedule Hero AI'}. All rights reserved.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This is a preview of your branded experience
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
